import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { confirmPopup } from "primereact/confirmpopup";
import { InputTextarea } from "primereact/inputtextarea";
import { OverlayPanel } from "primereact/overlaypanel";
import { useContext, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type {
  AppointmentRequest,
  AppointmentRequestWithoutRegistration,
} from "src/core/backend/clients/backendClient";
import { ToastContext } from "src/core/context/ToastContext";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useDate } from "src/core/hooks/useDate";
import { useError } from "src/core/hooks/useError";
import { useGtm } from "src/core/hooks/useGtm";
import { logService } from "src/core/services/logService";
import { textService } from "src/core/services/textService";
import { viewService } from "src/core/services/viewService";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { Login } from "src/features/auth/components/Login/Login";
import { useProfile } from "src/features/auth/hooks/useProfile";
import type { RegisterFormData } from "src/features/auth/pages/RegisterPage/RegisterPage";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import { AddressField } from "src/features/shared/components/AddressField";
import { BirthDateField } from "src/features/shared/components/BirthDateField";
import { EmailField } from "src/features/shared/components/EmailField";
import { GenderField } from "src/features/shared/components/GenderField";
import {
  NameField,
  type NameRef,
} from "src/features/shared/components/NameField";
import { Password2Field } from "src/features/shared/components/Password2Field";
import { PasswordField } from "src/features/shared/components/PasswordField";
import {
  PhoneField,
  type PhoneRef,
} from "src/features/shared/components/PhoneField";
import { PrivacyAcceptField } from "src/features/shared/components/PrivacyAcceptField";
import { SSNField } from "src/features/shared/components/SSNField";
import type { AppointmentStepProps } from "../../AppointmentPage";
import "./AppointmentStep5.css";

export interface FinalizationFormData extends RegisterFormData {
  notes?: string;
  acceptance: boolean;
  permanentRegistration: boolean;
}

export function AppointmentStep5({
  activeStepIndex,
  setActiveStepIndex,
}: AppointmentStepProps) {
  const toastRef = useContext(ToastContext);
  const nameRef = useRef<NameRef>(null);
  const phoneRef = useRef<PhoneRef>(null);
  const notesRef = useRef<HTMLElement>(null);
  const loginOverlayRef = useRef<OverlayPanel | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dateTimeFormat } = useDate();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const appointmentStore = useAppointmentStore();
  const settingsStore = useSettingsStore();
  const errorHandler = useErrorHandler();

  const gtm = useGtm();

  const { birthDateLabel, ssnLabel, addressLabel, phoneLabel } = useProfile();

  const form = useForm<FinalizationFormData>({
    mode: "all",
    defaultValues: {
      name: "",
      email: "",

      phone: "",
      notes: "",
      acceptance: false,
      permanentRegistration: false,

      password: "",
      password2: "",
      birthDate: undefined,
      gender: undefined,
      ssn: undefined,
      address: {
        postalCode: undefined,
        city: "",
        streetAddress: "",
      },

      dataPrivacyNoticeAccepted: false,
    },
  });

  const permanent = form.watch("permanentRegistration");

  const onPermanentRegistrationTurnOff = () => {
    form.unregister("name");
    form.unregister("email");
    form.unregister("address.postalCode");
    form.unregister("address.city");
    form.unregister("address.streetAddress");
  };

  useEffect(() => {
    appointmentStore.setQuickAppointment(false);

    if (!appointmentStore.selectedDateTime) {
      setActiveStepIndex(--activeStepIndex);
      return;
    }

    if (appointmentStore.lastAppointmentRequest) {
      form.reset({
        ...appointmentStore.lastAppointmentRequest,
      } as unknown as FinalizationFormData);
    } else if (appointmentStore.lastAppointmentRequestWithoutRegistration) {
      form.reset({
        ...appointmentStore.lastAppointmentRequestWithoutRegistration,
      } as unknown as FinalizationFormData);
    } else {
      const formattedPhone = textService.formatPhone(
        authStore.user.phone ?? "+36"
      );

      form.reset({
        phone: formattedPhone,
      } as unknown as FinalizationFormData);
    }

    const phoneValue = form.getValues("phone");

    if (!viewService.isMobileSize) {
      if (!authStore.user.isLoggedIn) {
        nameRef.current?.focus();
      } else if ((phoneValue?.length ?? 0) < 6) {
        phoneRef.current?.focus();
      } else {
        notesRef.current?.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (!permanent) {
      onPermanentRegistrationTurnOff();
    }
  }, [permanent]);

  const onFinalize = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!form.formState.isValid) return;

    const formValues = form.getValues();

    logService.log("AppointmentStep5.onFinalize", formValues);

    confirmPopup({
      target: event.currentTarget,
      message: t("Home.Appointments.FinalizeSure"),
      icon: "pi pi-question-circle",
      accept: async () => {
        if (authStore.user?.isLoggedIn) {
          const request: AppointmentRequest = {
            participantId: appointmentStore.selectedPerson?.id,
            profileId: appointmentStore.selectedDoctorProfile?.profileId,
            dateTime: appointmentStore.selectedDateTime,
            notes: formValues["notes"],
            isTelemedical: false,
            selectedUniqeOption: undefined,
            phone: formValues["phone"],
          };

          appointmentStore
            .appointmentPost(request)
            .then(() => {
              gtm.event("appointment_post", {});

              toastRef?.current?.show({
                severity: "success",
                summary: settingsStore.legacySettings
                  ?.appointmentActivationRequired
                  ? t("Home.Appointments.FinalizeSuccessTitle")
                  : t("Home.Appointments.Title"),
                detail: settingsStore.legacySettings
                  ?.appointmentActivationRequired
                  ? t("Home.Appointments.FinalizeSuccess")
                  : t("Home.Appointments.FinalizeSuccessTitle"),
                life: 5000,
              });

              appointmentStore.clear();
              appointmentStore.appointmentList();

              navigate("/home");
            })
            .catch((error) => {
              errorHandler.setError({
                title: t("Home.Appointments.Title"),
                message:
                  t("Home.Appointments.FinalizeFailed") +
                  " " +
                  getErrorMessage(error),
              });
            });
        } else {
          const request: AppointmentRequestWithoutRegistration = {
            ...formValues,
          };

          if (!permanent) request.dataPrivacyNoticeAccepted = true;
          request.participantId = appointmentStore.selectedPerson?.id;
          request.profileId = appointmentStore.selectedDoctorProfile?.profileId;
          request.dateTime = appointmentStore.selectedDateTime;
          request.isTelemedical = false;
          request.selectedUniqeOption = undefined;

          appointmentStore
            .appointmentCreateWithoutRegistration(request)
            .then(() => {
              gtm.event("appointment_post", {});

              toastRef?.current?.show({
                severity: "success",
                summary: settingsStore.legacySettings
                  ?.appointmentActivationRequired
                  ? t("Home.Appointments.FinalizeSuccessTitle")
                  : t("Home.Appointments.Title"),
                detail: settingsStore.legacySettings
                  ?.appointmentActivationRequired
                  ? t("Home.Appointments.FinalizeSuccess")
                  : t("Home.Appointments.FinalizeSuccessTitle"),
                life: 5000,
              });

              appointmentStore.clear();

              navigate("/");
            })
            .catch((error) => {
              errorHandler.setError({
                title: t("Home.Appointments.Title"),
                message:
                  t("Home.Appointments.FinalizeFailed") +
                  " " +
                  getErrorMessage(error),
              });
            });
        }
      },
    });
  };

  const onAbandon = async (event: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event.currentTarget,
      message: t("Home.Appointments.AbandonSure"),
      icon: "pi pi-question-circle",
      accept: async () => {
        appointmentStore.clear();

        navigate(authStore.user?.isLoggedIn ? "/home" : "/");
      },
    });
  };

  const notesLabel =
    t("Home.Appointments.Notes") +
    (settingsStore.legacySettings?.commentRequired ?? false ? " *" : "");

  const hideLogin =
    settingsStore.clientConfiguration.subMode?.includes("hidelogin");

  const hideAppointmentReg =
    settingsStore.clientConfiguration.subMode?.includes("hideappointmentreg");

  return (
    <form>
      <div className="appointmentStep5">
        <div className="p-1 flex justify-content-between">
          <div>
            <Button
              type="button"
              icon="pi pi-chevron-left"
              label={t("Home.Appointments.Step4Title")}
              onClick={() => {
                setActiveStepIndex(--activeStepIndex);
              }}
            ></Button>
          </div>
          <div className="flex-grow-1"></div>
          <div></div>
        </div>
      </div>

      <div className="grid justify-content-center">
        {!authStore.user?.isLoggedIn && (
          <div className="col-12 lg:col-8 xl:col-3">
            <Card
              title={hideLogin ? "" : t("Home.Appointments.Title")}
              className="card"
            >
              <div className="flex flex-column gap-1">
                <div
                  className="font-bold"
                  style={{ color: "var(--orange-600)" }}
                  dangerouslySetInnerHTML={{
                    __html: t("Home.Appointments.Step5RegInfo"),
                  }}
                ></div>

                {!hideLogin && (
                  <Button
                    type="button"
                    label={t("Home.Login.Title")}
                    onClick={(event) => {
                      loginOverlayRef.current?.toggle(event);
                    }}
                  ></Button>
                )}
              </div>
            </Card>
          </div>
        )}

        <div className="col-12 lg:col-8 xl:col-5">
          <Card
            title={t("Home.Appointments.Step5Title")}
            className="card"
            footer={
              <div className="grid">
                <span className="col-4 hidden md:block"></span>
                <div className="col-12 md:col-8 flex justify-content-between gap-2">
                  <Button
                    type="button"
                    icon="pi pi-check"
                    label={t("Home.Appointments.Finalize")}
                    severity="success"
                    loading={appointmentStore.loading}
                    disabled={!form.formState.isValid}
                    onClick={(event) => {
                      onFinalize(event);
                    }}
                  ></Button>

                  <Button
                    type="button"
                    icon="pi pi-times"
                    label={t("Home.Appointments.Abandon")}
                    severity="warning"
                    onClick={(event) => {
                      onAbandon(event);
                    }}
                  ></Button>
                </div>
              </div>
            }
          >
            <div className="field grid">
              <label className="col-12 md:col-4 font-bold">
                {t("Home.Appointments.FinalizePerson")}:
              </label>
              <div className="col-12 md:col-8">
                <span>{appointmentStore.selectedPerson?.name}</span>
              </div>
            </div>
            <div className="field grid">
              <label className="col-12 md:col-4 font-bold">
                {t("Home.Appointments.FinalizeClinic")}:
              </label>
              <div className="col-12 md:col-8">
                <span>{appointmentStore.selectedClinic?.name}</span>
              </div>
            </div>
            <div className="field grid">
              <label className="col-12 md:col-4 font-bold">
                {t("Home.Appointments.FinalizeDoctor")}:
              </label>
              <div className="col-12 md:col-8">
                <span>
                  {appointmentStore.selectedDoctorProfile?.doctorName}
                </span>
              </div>
            </div>
            {(appointmentStore.selectedClinic?.address?.length ?? 0) > 0 && (
              <div className="field grid">
                <label className="col-12 md:col-4 font-bold">
                  {t("Home.Appointments.FinalizeAddress")}:
                </label>
                <div className="col-12 md:col-8">
                  <span>{appointmentStore.selectedClinic?.address}</span>
                </div>
              </div>
            )}

            {(appointmentStore.selectedDoctorProfile?.profileActivity?.trim()
              .length ?? 0) > 0 && (
              <div className="field grid">
                <label className="col-12 md:col-4 font-bold">
                  {t("Home.Appointments.FinalizeActivity")}:
                </label>
                <div className="col-12 md:col-8">
                  <span>
                    {appointmentStore.selectedDoctorProfile?.profileActivity}
                  </span>
                </div>
              </div>
            )}
            {(appointmentStore.selectedDoctorProfile?.profileDescription
              ?.length ?? 0) > 0 && (
              <div className="field grid">
                <label className="col-12 md:col-4 font-bold">
                  {t("Home.Appointments.FinalizeDescription")}:
                </label>
                <div className="col-12 md:col-8 font-bold">
                  <span style={{ color: "var(--red-600)" }}>
                    {appointmentStore.selectedDoctorProfile?.profileDescription}
                  </span>
                </div>
              </div>
            )}

            <div className="field grid">
              <label className="col-12 md:col-4 font-bold">
                {t("Home.Appointments.FinalizeDatetime")}:
              </label>
              <div className="col-12 md:col-8 font-bold">
                <span>
                  {textService.formatDate(
                    appointmentStore.selectedDateTime,
                    dateTimeFormat
                  )}
                </span>
              </div>
            </div>

            {!authStore.user?.isLoggedIn && (
              <>
                <div className="field grid">
                  <label
                    htmlFor="name"
                    className="col-12 md:col-4 font-bold"
                  >
                    {t("Home.Registration.Name")} *:
                  </label>
                  <div className="col-12 md:col-8">
                    <NameField
                      ref={nameRef}
                      form={form}
                      required={true}
                    />
                  </div>
                </div>

                <div className="field grid">
                  <label
                    htmlFor="email"
                    className="col-12 md:col-4 font-bold"
                  >
                    {t("Home.Login.Email")} *:
                  </label>
                  <div className="col-12 md:col-8">
                    <EmailField
                      form={form}
                      required={true}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="field grid">
              <label
                htmlFor="phone"
                className="col-12 md:col-4 font-bold"
              >
                {phoneLabel} :
              </label>
              <div className="col-12 md:col-8">
                <PhoneField
                  ref={phoneRef}
                  form={form}
                  required={settingsStore.legacySettings.phoneRequired ?? false}
                />
              </div>
            </div>

            <div className="field grid">
              <label
                htmlFor="fin_notes"
                className="col-12 md:col-4 font-bold"
              >
                {notesLabel}:
              </label>
              <div className="col-12 md:col-8">
                <InputTextarea
                  id="notes"
                  className="w100"
                  rows={5}
                  invalid={!!form.formState.errors.notes}
                  placeholder={t("Home.Appointments.NotesPlaceholder")}
                  {...form.register("notes", {
                    required: settingsStore.legacySettings.commentRequired
                      ? t("Error.Required")
                      : false,
                    minLength: {
                      value: settingsStore.legacySettings.commentRequired
                        ? 6
                        : 0,
                      message: t("Error.MinLength", { value: 6 }),
                    },
                  })}
                  ref={(el) => {
                    notesRef.current = el;
                    form.register("notes").ref(el);
                  }}
                />
                {form.formState.errors.notes && (
                  <small className="p-error block">
                    {form.formState.errors.notes.message}
                  </small>
                )}
              </div>
            </div>

            <div className="grid">
              <span className="col-4 hidden md:block"></span>
              <div className="col-12 md:col-8 flex justify-content-start align-items-center gap-1">
                <Controller
                  name="acceptance"
                  control={form.control}
                  rules={{
                    required: t("Error.Required"),
                  }}
                  render={({ field }) => (
                    <Checkbox
                      inputId="accept"
                      {...field}
                      checked={field.value ?? false}
                      onChange={(e) => field.onChange(e.checked ?? false)}
                    />
                  )}
                />
                <span
                  className=""
                  dangerouslySetInnerHTML={{
                    __html: t("Home.Appointments.Step5Acceptance"),
                  }}
                ></span>
              </div>
            </div>

            {!authStore.user?.isLoggedIn && !hideAppointmentReg && (
              <>
                <div className="grid">
                  <span className="col-4 hidden md:block"></span>
                  <div className="col-12 md:col-8 flex justify-content-start align-items-center gap-1">
                    <Controller
                      name="permanentRegistration"
                      control={form.control}
                      render={({ field }) => (
                        <Checkbox
                          inputId="permanentRegistration"
                          {...field}
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.checked ?? false)}
                        />
                      )}
                    />
                    <span className="font-bold">
                      {t("Home.Appointments.Step5RegCheck")}
                    </span>
                  </div>
                </div>

                {permanent && (
                  <>
                    <div className="field grid">
                      <label
                        htmlFor="password"
                        className="col-12 md:col-4 font-bold"
                      >
                        {t("Home.Login.Password")} *:
                      </label>
                      <div className="col-12 md:col-8">
                        <PasswordField
                          form={form}
                          required={permanent}
                          feedback={true}
                        />
                      </div>
                    </div>

                    <div className="field grid">
                      <label
                        htmlFor="password2"
                        className="col-12 md:col-4 font-bold white-space-nowrap"
                      >
                        {t("Home.Registration.Password2")} *:
                      </label>
                      <div className="col-12 md:col-8">
                        <Password2Field
                          form={form}
                          required={permanent}
                        />
                      </div>
                    </div>

                    <div className="field grid">
                      <label
                        htmlFor="birthDate"
                        className="col-12 md:col-4 font-bold"
                      >
                        {birthDateLabel} :
                      </label>
                      <div className="col-12 md:col-8">
                        <BirthDateField
                          form={form}
                          required={
                            permanent &&
                            (settingsStore.legacySettings.birthDateRequired ??
                              false)
                          }
                        />
                      </div>
                    </div>

                    <div className="field grid">
                      <label className="col-12 md:col-4 font-bold">
                        {t("Home.Registration.Gender")} *:
                      </label>
                      <div className="col-12 md:col-8 flex gap-2">
                        <GenderField
                          form={form}
                          required={permanent}
                        />
                      </div>
                    </div>

                    <div className="field grid">
                      <label
                        htmlFor="ssn"
                        className="col-12 md:col-4 font-bold"
                      >
                        {ssnLabel} :
                      </label>
                      <div className="col-12 md:col-8">
                        <SSNField
                          form={form}
                          required={
                            permanent &&
                            (settingsStore.legacySettings.ssnRequired ?? false)
                          }
                        />
                      </div>
                    </div>

                    <div className="field grid">
                      <label className="col-12 font-bold text-center">
                        {t("Home.Registration.SSNInfo")}
                      </label>
                    </div>

                    <div className="field grid">
                      <label className="col-12 md:col-4 font-bold">
                        {addressLabel} :
                      </label>
                      <div className="col-12 md:col-8">
                        <AddressField
                          key={
                            permanent ? "address-required" : "address-optional"
                          }
                          form={form}
                          required={
                            permanent &&
                            (settingsStore.legacySettings.addressRequired ??
                              false)
                          }
                        />
                      </div>
                    </div>

                    <PrivacyAcceptField
                      form={form}
                      required={permanent}
                    />
                  </>
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      <OverlayPanel
        ref={loginOverlayRef}
        dismissable={false}
        showCloseIcon
        style={{
          maxWidth: "350px",
          right: "10px",
        }}
      >
        <div className="p-overlaypanel-title">{t("Home.Login.Title")}</div>

        <Login
          registerVisible={false}
          forgotPasswordVisible={false}
          afterLogin={() => {
            form.setValue("permanentRegistration", false);
            onPermanentRegistrationTurnOff();
            loginOverlayRef.current?.hide();
          }}
        />
      </OverlayPanel>
    </form>
  );
}
